import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Task {
  id: number;
  title: string;
  time: string;
  difficulty: number;
  status: 'pending' | 'in-progress' | 'completed';
  category: 'quick' | 'medium' | 'complex';
  dependencies: number[];
  steps: string[];
  impact: 'Critical' | 'High' | 'Medium' | 'Low';
}

export default function AdminTaskFlow() {
  const navigate = useNavigate();
  const [activePhase, setActivePhase] = useState(0);
  const [completedTasks, setCompletedTasks] = useState<number[]>([]);
  const [currentTask, setCurrentTask] = useState<number | null>(null);
  const [dataFlow, setDataFlow] = useState<string[]>([]);
  const [animateData, setAnimateData] = useState(false);

  const tasks: Task[] = [
    // Quick Wins
    {
      id: 1,
      title: "Create Admin Route Structure",
      time: "30 min",
      difficulty: 1,
      status: 'pending',
      category: 'quick',
      dependencies: [],
      steps: ["Add routes", "Create placeholders", "Add guards"],
      impact: 'High'
    },
    {
      id: 2,
      title: "System Stats API",
      time: "45 min",
      difficulty: 1,
      status: 'pending',
      category: 'quick',
      dependencies: [],
      steps: ["Query D1", "Query R2", "Return JSON"],
      impact: 'High'
    },
    {
      id: 3,
      title: "Admin Login UI",
      time: "1 hour",
      difficulty: 2,
      status: 'pending',
      category: 'quick',
      dependencies: [1],
      steps: ["Design form", "Add validation", "Store token"],
      impact: 'Critical'
    },
    {
      id: 4,
      title: "Stats Dashboard Cards",
      time: "1 hour",
      difficulty: 2,
      status: 'pending',
      category: 'quick',
      dependencies: [2],
      steps: ["Create cards", "Fetch data", "Add animations"],
      impact: 'High'
    },
    {
      id: 5,
      title: "Basic File Listing",
      time: "1.5 hours",
      difficulty: 2,
      status: 'pending',
      category: 'quick',
      dependencies: [2],
      steps: ["File list API", "Data table", "Pagination"],
      impact: 'High'
    },
    // Medium Effort
    {
      id: 6,
      title: "Admin Authentication",
      time: "2 hours",
      difficulty: 3,
      status: 'pending',
      category: 'medium',
      dependencies: [3],
      steps: ["Create table", "JWT verify", "Middleware", "Protect routes"],
      impact: 'Critical'
    },
    {
      id: 7,
      title: "Receiver Management",
      time: "2.5 hours",
      difficulty: 3,
      status: 'pending',
      category: 'medium',
      dependencies: [6],
      steps: ["Query all", "Sortable table", "Search/filter", "Bulk actions"],
      impact: 'High'
    },
    {
      id: 8,
      title: "Activity Timeline",
      time: "3 hours",
      difficulty: 3,
      status: 'pending',
      category: 'medium',
      dependencies: [6],
      steps: ["Log table", "Track actions", "Timeline UI", "Real-time"],
      impact: 'Medium'
    },
    {
      id: 9,
      title: "Storage Analytics",
      time: "3 hours",
      difficulty: 3,
      status: 'pending',
      category: 'medium',
      dependencies: [2],
      steps: ["Calculate usage", "Group types", "Create charts", "Add alerts"],
      impact: 'High'
    },
    {
      id: 10,
      title: "Email Service",
      time: "3.5 hours",
      difficulty: 4,
      status: 'pending',
      category: 'medium',
      dependencies: [],
      steps: ["Setup Resend", "Templates", "Service", "Test"],
      impact: 'High'
    },
    // Complex Tasks
    {
      id: 11,
      title: "User Management",
      time: "4 hours",
      difficulty: 4,
      status: 'pending',
      category: 'complex',
      dependencies: [7],
      steps: ["Track users", "Profiles", "Block/unblock", "Export"],
      impact: 'Medium'
    },
    {
      id: 12,
      title: "Data Export System",
      time: "4 hours",
      difficulty: 4,
      status: 'pending',
      category: 'complex',
      dependencies: [5, 7],
      steps: ["CSV generator", "JSON export", "Zip bundles", "Schedule"],
      impact: 'Medium'
    },
    {
      id: 13,
      title: "Real-time Monitoring",
      time: "5 hours",
      difficulty: 5,
      status: 'pending',
      category: 'complex',
      dependencies: [8],
      steps: ["SSE endpoint", "Stream data", "Live dash", "Notifications"],
      impact: 'Low'
    },
    {
      id: 14,
      title: "Advanced Security",
      time: "6 hours",
      difficulty: 5,
      status: 'pending',
      category: 'complex',
      dependencies: [6],
      steps: ["Rate limit", "2FA", "Audit logs", "IP whitelist"],
      impact: 'High'
    },
    {
      id: 15,
      title: "Automated Reporting",
      time: "6 hours",
      difficulty: 5,
      status: 'pending',
      category: 'complex',
      dependencies: [10, 12],
      steps: ["Cron triggers", "Generate", "Send email", "Archive"],
      impact: 'Medium'
    }
  ];

  const phases = [
    { name: "Foundation", tasks: [1, 2, 3, 4], time: "3.25 hours", color: "from-green-400 to-green-600" },
    { name: "Core Features", tasks: [5, 6, 7], time: "6 hours", color: "from-blue-400 to-blue-600" },
    { name: "Enhanced", tasks: [8, 9], time: "6 hours", color: "from-purple-400 to-purple-600" },
    { name: "Communication", tasks: [10, 11], time: "7.5 hours", color: "from-orange-400 to-orange-600" },
    { name: "Advanced", tasks: [12, 13, 14, 15], time: "21 hours", color: "from-red-400 to-red-600" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimateData(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const simulateTask = (taskId: number) => {
    setCurrentTask(taskId);
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Animate through steps
    let stepIndex = 0;
    const stepInterval = setInterval(() => {
      if (stepIndex < task.steps.length) {
        setDataFlow(prev => [...prev, `${task.title}: ${task.steps[stepIndex]}`]);
        stepIndex++;
      } else {
        clearInterval(stepInterval);
        setCompletedTasks(prev => [...prev, taskId]);
        setCurrentTask(null);
      }
    }, 500);
  };

  const getDifficultyStars = (difficulty: number) => {
    return '⭐'.repeat(difficulty);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'quick': return 'bg-green-100 text-green-800 border-green-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'complex': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'Critical': return 'text-red-600 font-bold';
      case 'High': return 'text-orange-600 font-semibold';
      case 'Medium': return 'text-blue-600';
      case 'Low': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="mb-6 text-gray-300 hover:text-white flex items-center gap-2"
        >
          ← Back to FastFiles
        </button>

        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Admin Dashboard Implementation Flow
          </h1>
          <p className="text-xl text-gray-300">Interactive visualization of development tasks</p>
        </div>

        {/* Phase Timeline */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Implementation Phases</h2>
          <div className="flex gap-2 overflow-x-auto pb-4">
            {phases.map((phase, index) => (
              <button
                key={index}
                onClick={() => setActivePhase(index)}
                className={`relative px-6 py-4 rounded-xl transition-all transform hover:scale-105 ${
                  activePhase === index ? 'scale-105 shadow-2xl' : 'opacity-75'
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${phase.color} rounded-xl opacity-90`}></div>
                <div className="relative">
                  <div className="font-bold text-lg">{phase.name}</div>
                  <div className="text-sm opacity-90">{phase.time}</div>
                  <div className="text-xs mt-1">{phase.tasks.length} tasks</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Task Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {tasks
            .filter(task => phases[activePhase].tasks.includes(task.id))
            .map(task => (
              <div
                key={task.id}
                className={`relative bg-gray-800 bg-opacity-50 backdrop-blur rounded-xl p-6 border transition-all ${
                  completedTasks.includes(task.id)
                    ? 'border-green-500 shadow-green-500/20'
                    : currentTask === task.id
                    ? 'border-yellow-500 shadow-yellow-500/20 animate-pulse'
                    : 'border-gray-700 hover:border-gray-600'
                } shadow-xl cursor-pointer transform hover:scale-105`}
                onClick={() => simulateTask(task.id)}
              >
                {/* Status indicator */}
                <div className="absolute -top-2 -right-2">
                  {completedTasks.includes(task.id) ? (
                    <span className="text-2xl">✅</span>
                  ) : currentTask === task.id ? (
                    <span className="text-2xl animate-spin">⚙️</span>
                  ) : (
                    <span className="text-2xl">⏳</span>
                  )}
                </div>

                <h3 className="text-lg font-bold mb-2">{task.title}</h3>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Time:</span>
                    <span className="text-blue-400">{task.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Difficulty:</span>
                    <span>{getDifficultyStars(task.difficulty)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Impact:</span>
                    <span className={getImpactColor(task.impact)}>{task.impact}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-xs text-gray-400 mb-2">Steps:</div>
                  <div className="space-y-1">
                    {task.steps.map((step, i) => (
                      <div
                        key={i}
                        className={`text-xs px-2 py-1 rounded transition-all ${
                          currentTask === task.id && i === Math.floor(dataFlow.length % task.steps.length)
                            ? 'bg-yellow-500 text-black'
                            : completedTasks.includes(task.id)
                            ? 'bg-green-900 text-green-300'
                            : 'bg-gray-700 text-gray-400'
                        }`}
                      >
                        {step}
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`mt-3 px-2 py-1 text-xs rounded-full text-center border ${getCategoryColor(task.category)}`}>
                  {task.category}
                </div>
              </div>
            ))}
        </div>

        {/* Data Flow Visualization */}
        <div className="bg-gray-900 bg-opacity-50 backdrop-blur rounded-xl p-6 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4">Data Flow Monitor</h2>
          <div className="bg-black rounded-lg p-4 font-mono text-sm h-64 overflow-y-auto">
            {dataFlow.length === 0 ? (
              <div className="text-gray-500">Click on tasks to see data flow...</div>
            ) : (
              dataFlow.slice(-10).map((flow, i) => (
                <div
                  key={i}
                  className="text-green-400 mb-1 animate-fadeIn"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  &gt; {flow}
                </div>
              ))
            )}
            {animateData && (
              <div className="text-yellow-400 animate-pulse">
                &gt; System monitoring active...
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 bg-opacity-50 backdrop-blur rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-green-400">{completedTasks.length}</div>
            <div className="text-gray-400">Completed</div>
          </div>
          <div className="bg-gray-800 bg-opacity-50 backdrop-blur rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-yellow-400">{currentTask ? 1 : 0}</div>
            <div className="text-gray-400">In Progress</div>
          </div>
          <div className="bg-gray-800 bg-opacity-50 backdrop-blur rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-blue-400">{15 - completedTasks.length}</div>
            <div className="text-gray-400">Remaining</div>
          </div>
          <div className="bg-gray-800 bg-opacity-50 backdrop-blur rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-purple-400">
              {Math.round((completedTasks.length / 15) * 100)}%
            </div>
            <div className="text-gray-400">Progress</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}